/**
 * Personal Account → Activity data access.
 *
 * Always scopes to the signed-in user. Tenant: audit_events where
 * entity_id = me (RLS also enforces). Platform: platform_security_events
 * where target_user_id = me — things done TO my account, mirroring the
 * tenant semantics (super-admin RLS is wider; the query constrains).
 */
import { supabase } from '../supabase';
import {
  listPlatformSecurityEventsByCursor,
  countPlatformSecurityEvents,
} from './platformSecurityEvents';

/** Employee event types that are "about my access" (matches RLS allowlist). */
export const PERSONAL_EMPLOYEE_EVENT_TYPES = Object.freeze([
  'mfa_disabled',
  'role_changed',
  'deactivated',
  'reactivated',
  'deleted',
  'ownership_transferred',
]);

/** Auth event types shown on Account → Activity. */
export const PERSONAL_AUTH_EVENT_TYPES = Object.freeze([
  'login_failed',
  'mfa_enrolled',
  'mfa_disabled',
  // RD-SEC-015: written by the mfa_factor_lifecycle_sweeper (added/removed a
  // non-last factor) and replace_mfa_recovery_codes (issued a fresh set vs.
  // replaced an existing set) — both server-only, no client write site.
  'mfa_factor_added',
  'mfa_factor_removed',
  'mfa_recovery_codes_generated',
  'mfa_recovery_codes_regenerated',
  'mfa_recovery_used',
  'mfa_email_recovery_sent',
  'mfa_email_recovery_used',
  'mfa_verify_failed',
  'password_reset_sent',
  'password_changed',
  'session_revoked',
  'sessions_revoked_others',
]);

/**
 * Platform events shown on the personal Account → Activity feed — things
 * done TO my platform account (target_user_id = me), mirroring the tenant
 * feed's entity_id = me semantics. Single source of truth for that filter,
 * defined here (next to its tenant siblings PERSONAL_AUTH_EVENT_TYPES /
 * PERSONAL_EMPLOYEE_EVENT_TYPES) and RE-EXPORTED from accountEventGroups.js
 * (its historical export site — AccountPlatformActivityFeed.js and
 * accountEventGroups.test.js both still import it from there) so the feed's
 * event chips and this file's default query filter can't silently drift
 * apart.
 *
 * EXPLICIT literal (not derived from PLATFORM_EVENT_META) — deliberate.
 * A predicate-based derivation (category === 'security' || entityKind ===
 * 'auth' || labelId includes 'platform_user') previously swept in events
 * whose emitter can NEVER set target_user_id = the viewer (e.g.
 * platform_remove_tenant_member, platform_rotate_tenant_default_admin,
 * live_session_gate_reapplied — actor/target both null at the emitter), so
 * the personal feed silently showed admin actions performed on OTHER
 * people/tenants. An explicit list fails CLOSED: a future PLATFORM_EVENT_META
 * entry cannot join this feed until someone deliberately adds it here, after
 * confirming its emitter actually sets target_user_id.
 */
export const PLATFORM_PERSONAL_EVENT_TYPES = Object.freeze([
  'platform_password_changed',
  'platform_mfa_enrolled',
  'platform_mfa_disabled',
  // RD-SEC-015: platform-scope siblings of the tenant additions above, same
  // server-only emitters (mfa_sweep_emit / replace_mfa_recovery_codes).
  'platform_mfa_factor_added',
  'platform_mfa_factor_removed',
  'platform_mfa_recovery_codes_generated',
  'platform_mfa_recovery_codes_regenerated',
  'platform_password_reset_sent',
  'platform_mfa_recovery_code_used',
  'platform_mfa_email_recovery_used',
  'platform_mfa_verify_failed',
  'platform_session_revoked',
  'platform_sessions_revoked_others',
  'platform_mfa_reset',
  'platform_user_created',
  'platform_user_updated',
  'platform_user_deactivated',
  'platform_user_reactivated',
  // Symmetry with the tenant list, which likewise includes 'deleted'.
  'platform_delete_platform_user',
  // Tenant parity: tenant sibling 'login_failed' is already in
  // PERSONAL_AUTH_EVENT_TYPES above. log-login-failed sets target_user_id =
  // self for this event, mirroring the self-service auth events.
  'platform_login_failed',
]);

const SELECT_COLS =
  'id, tenant_id, entity_type, entity_id, event_type, actor_id, actor_name, actor_role, payload, created_at';

/**
 * event_type comparison clause for the personal OR filter below — `.eq` for
 * a single value, `.in.(...)` for an array (an event-type GROUP token
 * resolved upstream via accountEventGroups.js's resolveAccountEventFilter).
 * Mirrors the array support auditEvents.js already has for entityType/eventType.
 */
function eventTypeClause(eventType) {
  if (Array.isArray(eventType)) return `event_type.in.(${eventType.join(',')})`;
  return `event_type.eq.${eventType}`;
}

/**
 * True when the caller asked for an EMPTY set of event types (a resolved group
 * that came back empty). That means "match nothing" — falling through to the
 * default allowlist would silently return every allowed event instead, i.e.
 * fail OPEN. The platform service fails closed on the same input; both seams
 * now agree. Unreachable today (every group's events array is a non-empty
 * frozen literal) — this is the latent-case guard.
 */
function isEmptyEventSelection(eventType) {
  return Array.isArray(eventType) && eventType.length === 0;
}
const NEVER_MATCH_CLAUSE = 'event_type.in.()';

/**
 * PostgREST OR clause for personal entity/event allowlist.
 * Combined with entity_id.eq.userId via separate .eq().
 * `eventType` accepts a single event string OR an array (group).
 */
function personalEntityOrFilter(entityType, eventType) {
  const empTypes = PERSONAL_EMPLOYEE_EVENT_TYPES.join(',');
  const authTypes = PERSONAL_AUTH_EVENT_TYPES.join(',');
  if (isEmptyEventSelection(eventType)) {
    return `and(entity_type.eq.auth,${NEVER_MATCH_CLAUSE})`;
  }
  const hasEventFilter = Array.isArray(eventType)
    ? eventType.length > 0
    : Boolean(eventType) && eventType !== 'all';

  if (entityType === 'auth') {
    if (hasEventFilter) return `and(entity_type.eq.auth,${eventTypeClause(eventType)})`;
    return `and(entity_type.eq.auth,event_type.in.(${authTypes}))`;
  }
  if (entityType === 'employee') {
    if (hasEventFilter) {
      return `and(entity_type.eq.employee,${eventTypeClause(eventType)})`;
    }
    return `and(entity_type.eq.employee,event_type.in.(${empTypes}))`;
  }
  // all — auth (optionally narrowed) OR employee allowlist (optionally narrowed)
  if (hasEventFilter) {
    return [
      `and(entity_type.eq.auth,${eventTypeClause(eventType)})`,
      `and(entity_type.eq.employee,${eventTypeClause(eventType)})`,
    ].join(',');
  }
  return [
    `and(entity_type.eq.auth,event_type.in.(${authTypes}))`,
    `and(entity_type.eq.employee,event_type.in.(${empTypes}))`,
  ].join(',');
}

function applyCommonFilters(q, { search, fromIso, toIso }) {
  if (fromIso) q = q.gte('created_at', fromIso);
  if (toIso) q = q.lte('created_at', toIso);
  if (search && String(search).trim()) {
    const escaped = String(search).trim().replace(/[%,]/g, '\\$&');
    q = q.or(
      [
        `event_type.ilike.%${escaped}%`,
        `actor_name.ilike.%${escaped}%`,
        `payload->>email.ilike.%${escaped}%`,
      ].join(','),
    );
  }
  return q;
}

function applyCursor(q, cursor, ascending) {
  if (!cursor?.createdAt || !cursor?.id) return q;
  if (ascending) {
    return q.or(
      `created_at.gt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.gt.${cursor.id})`,
    );
  }
  return q.or(
    `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`,
  );
}

/**
 * @param {object} args
 * @param {string} args.userId
 * @param {'auth'|'employee'|'all'} [args.entityType]
 * @param {string} [args.eventType]
 * @param {string} [args.search]
 * @param {string} [args.fromIso]
 * @param {string} [args.toIso]
 * @param {number} [args.limit]
 * @param {object|null} [args.cursor]
 * @param {'asc'|'desc'} [args.sortOrder]
 */
export async function listPersonalSecurityEventsByCursor({
  userId,
  entityType = 'all',
  eventType,
  search,
  fromIso,
  toIso,
  limit = 50,
  cursor = null,
  sortOrder = 'desc',
} = {}) {
  if (!userId) return { data: [], nextCursor: null, error: new Error('userId required') };

  const ascending = sortOrder === 'asc';
  const entityOr = personalEntityOrFilter(entityType, eventType);

  let q = supabase
    .from('audit_events')
    .select(SELECT_COLS)
    .eq('entity_id', userId)
    .or(entityOr)
    .order('created_at', { ascending })
    .order('id', { ascending })
    .limit(limit + 1);

  q = applyCommonFilters(q, { search, fromIso, toIso });
  q = applyCursor(q, cursor, ascending);

  const { data, error } = await q;
  if (error) return { data: [], nextCursor: null, error };

  const rows = data || [];
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  const last = page[page.length - 1];
  return {
    data: page,
    nextCursor: hasMore && last ? { createdAt: last.created_at, id: last.id } : null,
    error: null,
  };
}

export async function countPersonalSecurityEvents({
  userId,
  entityType = 'all',
  eventType,
  search,
  fromIso,
  toIso,
} = {}) {
  if (!userId) return { count: 0, error: new Error('userId required') };

  const entityOr = personalEntityOrFilter(entityType, eventType);
  let q = supabase
    .from('audit_events')
    .select('*', { count: 'exact', head: true })
    .eq('entity_id', userId)
    .or(entityOr);

  q = applyCommonFilters(q, { search, fromIso, toIso });

  const { count, error } = await q;
  return { count: count ?? 0, error: error || null };
}

/**
 * Resolves the eventType arg for the platform personal feed against
 * PLATFORM_PERSONAL_EVENT_TYPES — this is an ALLOWLIST, not just a default:
 * an array (a resolved group, from resolveAccountEventFilter — which mixes
 * tenant + platform event strings, see accountEventGroups.js) is intersected
 * with the allowlist so the tenant-only legs are stripped (they can never
 * match a platform_security_events row anyway, but an unfiltered array would
 * still say so); a single explicit event string is passed through only if it
 * is IN the allowlist, else resolved to `[]` (fail closed — matches nothing
 * via `.in('event_type', [])`, never "no filter" and never the raw
 * unrecognized string); undefined/null/'all' falls back to the full
 * allowlist. This is what closes the original bug: platform Account →
 * Activity used to pass `null` here on the default view, which reached
 * listPlatformSecurityEventsByCursor with no event_type constraint at all —
 * failing OPEN. The tenant personal feed's personalEntityOrFilter has applied
 * an allowlist-by-default from day one; this brings the platform seam fully
 * in line with it, including the case an explicit-but-foreign event string
 * could otherwise widen the feed.
 */
function resolvePersonalPlatformEventType(eventType) {
  if (Array.isArray(eventType)) {
    return eventType.filter((e) => PLATFORM_PERSONAL_EVENT_TYPES.includes(e));
  }
  if (!eventType || eventType === 'all') return PLATFORM_PERSONAL_EVENT_TYPES;
  return PLATFORM_PERSONAL_EVENT_TYPES.includes(eventType) ? eventType : [];
}

/**
 * Platform Account → Activity: events done TO me (target_user_id = me),
 * restricted to PLATFORM_PERSONAL_EVENT_TYPES unless the caller passes an
 * explicit narrower filter (a resolved group array or a single event type).
 */
export async function listPersonalPlatformSecurityEventsByCursor({
  userId,
  eventType,
  search,
  fromIso,
  toIso,
  limit = 50,
  cursor = null,
  sortOrder = 'desc',
} = {}) {
  if (!userId) return { data: [], nextCursor: null, error: new Error('userId required') };
  return listPlatformSecurityEventsByCursor({
    targetUserId: userId,
    eventType: resolvePersonalPlatformEventType(eventType),
    search: search || '',
    fromIso: fromIso || null,
    toIso: toIso || null,
    limit,
    cursor,
    sortOrder,
  });
}

/**
 * Count counterpart of listPersonalPlatformSecurityEventsByCursor — MUST
 * receive identical filters (targetUserId + the same resolved eventType) or
 * the footer count disagrees with the rows actually shown.
 */
export async function countPersonalPlatformSecurityEvents({
  userId,
  eventType,
  search,
  fromIso,
  toIso,
} = {}) {
  if (!userId) return { count: 0, error: new Error('userId required') };
  return countPlatformSecurityEvents({
    targetUserId: userId,
    eventType: resolvePersonalPlatformEventType(eventType),
    search: search || '',
    fromIso: fromIso || null,
    toIso: toIso || null,
  });
}

/**
 * Paginated full fetch for Account → Activity export (cap 10k).
 */
export async function fetchAllPersonalSecurityEvents(filters = {}, maxRows = 10000) {
  const PAGE = 200;
  const out = [];
  let cursor = null;
  while (out.length < maxRows) {
    const remaining = maxRows - out.length;
    const limit = Math.min(PAGE, remaining);
    // eslint-disable-next-line no-await-in-loop
    const { data, nextCursor, error } = await listPersonalSecurityEventsByCursor({
      ...filters,
      limit,
      cursor,
    });
    if (error) return { events: out, capped: false, error };
    if (!data || data.length === 0) break;
    out.push(...data);
    if (!nextCursor) break;
    cursor = nextCursor;
  }
  return { events: out, capped: out.length >= maxRows, error: null };
}

export async function fetchAllPersonalPlatformSecurityEvents(filters = {}, maxRows = 10000) {
  const PAGE = 200;
  const out = [];
  let cursor = null;
  while (out.length < maxRows) {
    const remaining = maxRows - out.length;
    const limit = Math.min(PAGE, remaining);
    // eslint-disable-next-line no-await-in-loop
    const { data, nextCursor, error } = await listPersonalPlatformSecurityEventsByCursor({
      ...filters,
      limit,
      cursor,
    });
    if (error) return { events: out, capped: false, error };
    if (!data || data.length === 0) break;
    out.push(...data);
    if (!nextCursor) break;
    cursor = nextCursor;
  }
  return { events: out, capped: out.length >= maxRows, error: null };
}
